import { nextTick } from 'vue'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { toast } from 'vue3-toastify'
import type { Deliberation } from '@/types/deliberation'
import {
	buildFeuilleOutcomePdfFilename,
	buildResultatsSessionPdfFilename,
	getDeliberationOutcomeDocumentLabels,
	getDeliberationSessionLabel,
} from '@/utils/deliberationDocumentLabels'

// Réglages qualité PDF (un peu plus net, sans trop grossir).
const PDF_CANVAS_SCALE = 2
const PDF_JPEG_QUALITY = 0.9

/** Numérotation « 1/4 » en bas à droite (après le rendu image de la page courante). */
function addPdfPageNumberBottomRight(pdf: jsPDF, pageOneBased: number, totalPages: number) {
	if (totalPages < 1) return
	const w = pdf.internal.pageSize.getWidth()
	const h = pdf.internal.pageSize.getHeight()
	const marginX = 12
	const marginY = 8
	pdf.setFont('times', 'normal')
	pdf.setFontSize(10)
	pdf.setTextColor(45, 45, 45)
	pdf.text(`${pageOneBased}/${totalPages}`, w - marginX, h - marginY, { align: 'right' })
}

function pxToMm(px: number, pxPerMm: number): number {
	return px / pxPerMm
}

function mmToPx(mm: number, pxPerMm: number): number {
	return mm * pxPerMm
}

function getNumericPx(styleValue: string): number {
	const n = Number.parseFloat(styleValue || '0')
	return Number.isFinite(n) ? n : 0
}

function getElementVerticalMarginsPx(el: HTMLElement): number {
	const cs = window.getComputedStyle(el)
	return getNumericPx(cs.marginTop) + getNumericPx(cs.marginBottom)
}

function getSignatureReservePx(pageEl: HTMLElement): number {
	const signatureEl = pageEl.querySelector('.signature-space') as HTMLElement | null
	if (!signatureEl) return 0
	const reserve = signatureEl.offsetHeight + getElementVerticalMarginsPx(signatureEl)
	return reserve
}

function setA4PageBox(pageEl: HTMLElement) {
	// Force a predictable A4 layout for measurement/capture.
	pageEl.style.width = '210mm'
	pageEl.style.height = '297mm'
	pageEl.style.overflow = 'hidden'
	pageEl.style.boxSizing = 'border-box'
}

function setA4LandscapePageBox(pageEl: HTMLElement) {
	pageEl.style.width = '297mm'
	pageEl.style.height = '210mm'
	pageEl.style.overflow = 'hidden'
	pageEl.style.boxSizing = 'border-box'
}

function clonePageSkeletonWithEmptyTable(pageEl: HTMLElement): HTMLElement | null {
	const clone = pageEl.cloneNode(true) as HTMLElement

	// Remove any "page-break" helper classes that might affect layout.
	clone.classList.remove('page-break')

	const table = clone.querySelector('table.results-table') as HTMLTableElement | null
	if (!table) return null

	const tbody = table.querySelector('tbody')
	if (tbody) tbody.innerHTML = ''

	return clone
}

function getPagePxPerMm(pageEl: HTMLElement): number {
	const rect = pageEl.getBoundingClientRect()
	const widthPx = rect.width || pageEl.offsetWidth
	return widthPx / 210
}

function paginateSinglePageByTableRows(pageEl: HTMLElement): HTMLElement[] {
	const originalTable = pageEl.querySelector('table.results-table') as HTMLTableElement | null
	if (!originalTable) return [pageEl]

	const originalTbody = originalTable.querySelector('tbody')
	const originalRows = Array.from(originalTbody?.querySelectorAll('tr') || [])
	if (originalRows.length === 0) return [pageEl]

	const pages: HTMLElement[] = []
	// The signature block is part of the page DOM; it naturally reduces available space.
	// We still read it once to ensure it's present and measurable.
	getSignatureReservePx(pageEl)

	// We build pages by cloning the full page (header/titles/table header),
	// then progressively adding rows until overflow.
	let currentPage = clonePageSkeletonWithEmptyTable(pageEl)
	if (!currentPage) return [pageEl]

	setA4PageBox(currentPage)
	pageEl.parentElement?.appendChild(currentPage) // attach for measurement

	const pxPerMm = getPagePxPerMm(currentPage)
	const pageHeightPx = mmToPx(297, pxPerMm)

	// Ensure current page is exactly A4 in pixels too (some browsers differ slightly).
	currentPage.style.height = `${pageHeightPx}px`

	let currentTable = currentPage.querySelector('table.results-table') as HTMLTableElement
	let currentTbody = currentTable.querySelector('tbody') as HTMLElement

	const signatureEl = currentPage.querySelector('.signature-space') as HTMLElement | null
	if (signatureEl) {
		// Keep the signature block at the bottom by ensuring it exists,
		// but the pagination constraint is handled by overflow check.
		signatureEl.style.display = ''
	}

	function isOverflowing(el: HTMLElement): boolean {
		// Flex + min-height:0 peut clipper le tableau sans augmenter scrollHeight.
		// On croise donc scrollHeight et collision tableau / zone signature.
		if (el.scrollHeight > el.clientHeight + 1) return true

		const signature = el.querySelector('.signature-space') as HTMLElement | null
		const table = el.querySelector('table.results-table') as HTMLElement | null
		if (signature && table) {
			const tableBottom = table.getBoundingClientRect().bottom
			const sigTop = signature.getBoundingClientRect().top
			if (tableBottom > sigTop - 2) return true
		}
		return false
	}

	for (const row of originalRows) {
		const rowClone = row.cloneNode(true) as HTMLTableRowElement
		currentTbody.appendChild(rowClone)
		// Force layout avant mesure (lignes EC multi-lignes).
		void currentPage.offsetHeight

		if (!isOverflowing(currentPage)) continue

		// Overflow: remove the last row and start a new page with that row.
		currentTbody.removeChild(rowClone)

		// If even the first row doesn't fit (shouldn't happen), keep it anyway.
		if (currentTbody.children.length === 0) {
			currentTbody.appendChild(rowClone)
		}

		pages.push(currentPage)

		currentPage = clonePageSkeletonWithEmptyTable(pageEl)
		if (!currentPage) break
		setA4PageBox(currentPage)
		pageEl.parentElement?.appendChild(currentPage)
		currentPage.style.height = `${pageHeightPx}px`

		currentTable = currentPage.querySelector('table.results-table') as HTMLTableElement
		currentTbody = currentTable.querySelector('tbody') as HTMLElement
		currentTbody.appendChild(rowClone)
		void currentPage.offsetHeight
	}

	// Push last page.
	if (currentPage) pages.push(currentPage)

	return pages
}

/** Découpe chaque `.page` contenant `table.results-table` en pages A4 (lignes + zone signature). */
export function buildPaginatedPages(rootEl: HTMLElement): HTMLElement[] {
	const originalPages = Array.from(rootEl.querySelectorAll('.page')) as HTMLElement[]
	if (originalPages.length === 0) return []

	// We'll create a new wrapper in the same host and generate paginated pages there.
	const host = rootEl.parentElement
	if (!host) return originalPages

	const paginatedWrapper = document.createElement('div')
	paginatedWrapper.className = 'paginated-wrapper'
	paginatedWrapper.style.width = '210mm'
	paginatedWrapper.style.background = '#ffffff'
	paginatedWrapper.style.boxSizing = 'border-box'

	host.appendChild(paginatedWrapper)

	const paginatedPages: HTMLElement[] = []
	for (const pageEl of originalPages) {
		const subPages = paginateSinglePageByTableRows(pageEl)
		for (const p of subPages) {
			// Ensure page boxes are consistent for capture.
			setA4PageBox(p)
			paginatedWrapper.appendChild(p)
			paginatedPages.push(p)
		}
	}

	// Remove original pages to avoid capturing duplicates.
	for (const pageEl of originalPages) {
		pageEl.remove()
	}

	return paginatedPages
}

function cloneSkeletonWithEmptyTbody(rootEl: HTMLElement, tableSelector: string): { page: HTMLElement; tbody: HTMLElement } | null {
	const clone = rootEl.cloneNode(true) as HTMLElement
	const table = clone.querySelector(tableSelector) as HTMLTableElement | null
	if (!table) return null
	const tbody = table.querySelector('tbody') as HTMLElement | null
	if (!tbody) return null
	tbody.innerHTML = ''
	return { page: clone, tbody }
}

/** Paginate one block (logo + title + rattrapage table) by tbody rows into fixed A4 landscape pages. */
function paginateRattrapageDocumentByRowsIntoWrapper(sourceRoot: HTMLElement, wrapper: HTMLElement): HTMLElement[] {
	const tableSelector = 'table.rattrapage-table'
	const originalTable = sourceRoot.querySelector(tableSelector) as HTMLTableElement | null
	if (!originalTable) return []

	const originalTbody = originalTable.querySelector('tbody')
	const originalRows = Array.from(originalTbody?.querySelectorAll('tr') || [])

	const pages: HTMLElement[] = []

	if (originalRows.length === 0) {
		const skeleton = cloneSkeletonWithEmptyTbody(sourceRoot, tableSelector)
		if (!skeleton) return []
		const page = skeleton.page
		setA4LandscapePageBox(page)
		wrapper.appendChild(page)
		const pxPerMm = getLandscapePxPerMm(page)
		page.style.height = `${mmToPx(210, pxPerMm)}px`
		pages.push(page)
		return pages
	}

	let skeleton = cloneSkeletonWithEmptyTbody(sourceRoot, tableSelector)
	if (!skeleton) return []

	let currentPage = skeleton.page
	let currentTbody = skeleton.tbody

	setA4LandscapePageBox(currentPage)
	wrapper.appendChild(currentPage)

	const pxPerMm = getLandscapePxPerMm(currentPage)
	const pageHeightPx = mmToPx(210, pxPerMm)
	currentPage.style.height = `${pageHeightPx}px`

	function isOverflowing(el: HTMLElement): boolean {
		return el.scrollHeight > el.clientHeight
	}

	for (const row of originalRows) {
		const rowClone = row.cloneNode(true) as HTMLTableRowElement
		currentTbody.appendChild(rowClone)

		if (!isOverflowing(currentPage)) continue

		currentTbody.removeChild(rowClone)

		if (currentTbody.children.length === 0) {
			currentTbody.appendChild(rowClone)
		}

		pages.push(currentPage)

		skeleton = cloneSkeletonWithEmptyTbody(sourceRoot, tableSelector)
		if (!skeleton) break
		currentPage = skeleton.page
		currentTbody = skeleton.tbody

		setA4LandscapePageBox(currentPage)
		wrapper.appendChild(currentPage)
		currentPage.style.height = `${pageHeightPx}px`
		currentTbody.appendChild(rowClone)
	}

	pages.push(currentPage)
	return pages
}

/** Plusieurs tableaux (lots d’EC) : on termine la pagination du lot courant avant le suivant. */
function paginateRattrapageFullDocument(sheetEl: HTMLElement): HTMLElement[] {
	const tableSelector = 'table.rattrapage-table'
	const batchRoots = Array.from(sheetEl.querySelectorAll('.rattrapage-batch-root')) as HTMLElement[]
	const hasTable = Boolean(sheetEl.querySelector(tableSelector))

	if (!hasTable && batchRoots.length === 0) {
		return [sheetEl]
	}

	const host = sheetEl.parentElement
	if (!host) return [sheetEl]

	const wrapper = document.createElement('div')
	wrapper.className = 'paginated-wrapper-landscape'
	wrapper.style.width = '297mm'
	wrapper.style.background = '#ffffff'
	wrapper.style.boxSizing = 'border-box'
	host.appendChild(wrapper)

	const allPages: HTMLElement[] = []

	if (batchRoots.length > 0) {
		for (const br of batchRoots) {
			allPages.push(...paginateRattrapageDocumentByRowsIntoWrapper(br, wrapper))
		}
	} else {
		allPages.push(...paginateRattrapageDocumentByRowsIntoWrapper(sheetEl, wrapper))
	}

	if (allPages.length === 0) {
		wrapper.remove()
		return [sheetEl]
	}

	sheetEl.remove()
	return allPages
}

function getLandscapePxPerMm(pageEl: HTMLElement): number {
	const rect = pageEl.getBoundingClientRect()
	const widthPx = rect.width || pageEl.offsetWidth
	return widthPx / 297
}

function cloneTableauDeliberationSkeleton(
	sourceRoot: HTMLElement,
	options: { withUeBlocks: boolean; withFooter: boolean }
): { page: HTMLElement; tbody: HTMLElement } | null {
	const clone = sourceRoot.cloneNode(true) as HTMLElement
	if (!options.withUeBlocks) {
		clone.querySelector('.delib-ue-blocks-section')?.remove()
	}
	if (!options.withFooter) {
		clone.querySelector('.delib-footer-section')?.remove()
	}
	const tbody = clone.querySelector('table.tableau-deliberation-table tbody') as HTMLElement | null
	if (!tbody) return null
	tbody.innerHTML = ''
	return { page: clone, tbody }
}

type TableauDelibPageLayout = {
	withUeBlocks: boolean
	withFooter: boolean
}

/** 0 = layout référence ; 1–2 = récap/signatures resserrés dynamiquement */
const TABLEAU_FOOTER_COMPACT_MAX = 2

/** Le contenu ne doit jamais empiéter sur la ligne « Delib. … » (doc-footer absolu). */
function tableauDelibContentOverlapsDocFooter(pageEl: HTMLElement): boolean {
	const docFooter = pageEl.querySelector('.doc-footer') as HTMLElement | null
	if (!docFooter) return false

	const footerTop = docFooter.getBoundingClientRect().top
	const contentEls = pageEl.querySelectorAll(
		'.main-table-wrap, .delib-footer-section, .delib-bottom-anchor'
	)

	for (const node of contentEls) {
		const el = node as HTMLElement
		const rect = el.getBoundingClientRect()
		if (rect.height <= 0) continue
		if (rect.bottom > footerTop - 0.5) return true
	}

	return false
}

function isTableauDelibPageOverflowing(el: HTMLElement): boolean {
	if (el.scrollHeight > el.clientHeight + 1) return true
	return tableauDelibContentOverlapsDocFooter(el)
}

/** Retire des lignes en bas tant que la page déborde ou touche le pied pagination. */
function trimTableauDelibPageOverflowRows(
	skeleton: { page: HTMLElement; tbody: HTMLElement },
	removeFooterOnTrim: boolean
): number {
	let removed = 0
	void skeleton.page.offsetHeight

	while (isTableauDelibPageOverflowing(skeleton.page)) {
		if (skeleton.tbody.children.length <= 1) break

		skeleton.tbody.removeChild(skeleton.tbody.lastElementChild!)
		removed++
		if (removeFooterOnTrim && removed === 1) {
			skeleton.page.querySelector('.delib-footer-section')?.remove()
		}
		void skeleton.page.offsetHeight
	}

	return removed
}

function applyTableauFooterCompactLevel(pageEl: HTMLElement, level: number) {
	pageEl.classList.remove('footer-compact-1', 'footer-compact-2')
	if (level >= 1) pageEl.classList.add('footer-compact-1')
	if (level >= 2) pageEl.classList.add('footer-compact-2')
}

/** Dernière page multi-pages : récap/signatures poussés en bas de page. */
function applyTableauFooterAnchorBottom(pageEl: HTMLElement, enabled: boolean) {
	if (enabled) pageEl.classList.add('footer-anchor-bottom')
	else pageEl.classList.remove('footer-anchor-bottom')
}

function mountTableauDelibMeasurePage(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	layout: TableauDelibPageLayout,
	footerSource: HTMLElement | null,
	footerCompact = 0,
	anchorFooterBottom = false
): { page: HTMLElement; tbody: HTMLElement } | null {
	const skeleton = cloneTableauDeliberationSkeleton(sourceRoot, {
		withUeBlocks: layout.withUeBlocks,
		withFooter: false,
	})
	if (!skeleton) return null

	setA4LandscapePageBox(skeleton.page)
	applyTableauFooterCompactLevel(skeleton.page, footerCompact)
	applyTableauFooterAnchorBottom(skeleton.page, anchorFooterBottom)
	wrapper.appendChild(skeleton.page)
	const pxPerMm = getLandscapePxPerMm(skeleton.page)
	skeleton.page.style.height = `${mmToPx(210, pxPerMm)}px`

	if (layout.withFooter && footerSource) {
		appendTableauDeliberationFooterSection(skeleton.page, footerSource)
	}

	return skeleton
}

/** Vérifie si `rowCount` lignes tiennent sur une page selon le layout (mesure DOM réelle). */
function tableauDelibPageFitsRowCount(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	rowCount: number,
	layout: TableauDelibPageLayout,
	footerSource: HTMLElement | null,
	rows: HTMLTableRowElement[],
	footerCompact = 0,
	anchorFooterBottom = false
): boolean {
	const skeleton = mountTableauDelibMeasurePage(
		sourceRoot,
		wrapper,
		layout,
		footerSource,
		footerCompact,
		anchorFooterBottom
	)
	if (!skeleton) return false

	for (let i = 0; i < rowCount; i++) {
		const row = rows[Math.min(i, rows.length - 1)]
		skeleton.tbody.appendChild(row.cloneNode(true) as HTMLTableRowElement)
	}

	void skeleton.page.offsetHeight
	const fits = !isTableauDelibPageOverflowing(skeleton.page)
	skeleton.page.remove()
	return fits
}

/** Capacité max de lignes pour un type de page (recherche dichotomique). */
function measureTableauDelibMaxRows(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	layout: TableauDelibPageLayout,
	footerSource: HTMLElement | null,
	rows: HTMLTableRowElement[],
	footerCompact = 0,
	anchorFooterBottom = false
): number {
	if (rows.length === 0) return 0

	let low = 0
	let high = rows.length

	while (low < high) {
		const mid = Math.ceil((low + high) / 2)
		if (
			tableauDelibPageFitsRowCount(
				sourceRoot,
				wrapper,
				mid,
				layout,
				footerSource,
				rows,
				footerCompact,
				anchorFooterBottom
			)
		) {
			low = mid
		} else {
			high = mid - 1
		}
	}

	return low
}

/** Moindre niveau compact permettant de tenir (priorité au layout le plus aéré). */
function findTableauFooterCompactLevelForSinglePage(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	rowCount: number,
	rows: HTMLTableRowElement[],
	footerSource: HTMLElement | null
): number | null {
	const layout: TableauDelibPageLayout = { withUeBlocks: true, withFooter: true }
	for (let level = 0; level <= TABLEAU_FOOTER_COMPACT_MAX; level++) {
		if (
			tableauDelibPageFitsRowCount(
				sourceRoot,
				wrapper,
				rowCount,
				layout,
				footerSource,
				rows,
				level,
				false
			)
		) {
			return level
		}
	}
	return null
}

/**
 * Moindre niveau compact pour la dernière page multi (0 = espacement normal aéré).
 * On ne serre le récap/signatures que si nécessaire pour faire tenir les lignes.
 */
function findTableauFooterCompactLevelForLastPage(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	rowCount: number,
	rows: HTMLTableRowElement[],
	footerSource: HTMLElement | null
): number {
	const layout: TableauDelibPageLayout = { withUeBlocks: false, withFooter: true }
	for (let level = 0; level <= TABLEAU_FOOTER_COMPACT_MAX; level++) {
		if (
			tableauDelibPageFitsRowCount(
				sourceRoot,
				wrapper,
				rowCount,
				layout,
				footerSource,
				rows,
				level,
				true
			)
		) {
			return level
		}
	}
	return TABLEAU_FOOTER_COMPACT_MAX
}

/** Capacité max dernière page (planification) — teste tous les niveaux compact. */
function measureTableauDelibMaxRowsOnLastPage(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	footerSource: HTMLElement | null,
	rows: HTMLTableRowElement[]
): number {
	const layout: TableauDelibPageLayout = { withUeBlocks: false, withFooter: true }
	let bestMax = 0

	for (let level = 0; level <= TABLEAU_FOOTER_COMPACT_MAX; level++) {
		const maxRows = measureTableauDelibMaxRows(
			sourceRoot,
			wrapper,
			layout,
			footerSource,
			rows,
			level,
			true
		)
		if (maxRows > bestMax) bestMax = maxRows
	}

	return bestMax
}

/**
 * Planifie le nombre d'étudiants par page (remplissage greedy) :
 * - 1ʳᵉ page : le maximum possible (UE incluses, sans récap)
 * - si tout tient sur 1ʳᵉ sans récap mais pas en page unique → décaler le minimum vers la 2ᵉ
 * - pages suivantes : remplir au maximum ; dernière page = reste + récap/signatures
 */
function lastPageFitsWithBestCompact(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	rowCount: number,
	rows: HTMLTableRowElement[],
	footerSource: HTMLElement | null
): boolean {
	const level = findTableauFooterCompactLevelForLastPage(
		sourceRoot,
		wrapper,
		rowCount,
		rows,
		footerSource
	)
	const lastLayout: TableauDelibPageLayout = { withUeBlocks: false, withFooter: true }
	return tableauDelibPageFitsRowCount(
		sourceRoot,
		wrapper,
		rowCount,
		lastLayout,
		footerSource,
		rows,
		level,
		true
	)
}

function planTableauDelibPageSplits(
	sourceRoot: HTMLElement,
	wrapper: HTMLElement,
	rows: HTMLTableRowElement[],
	footerSource: HTMLElement | null,
	capFirst: number,
	capMiddle: number
): { splits: number[]; lastPageCompact: number } {
	const total = rows.length
	if (total <= 0) return { splits: [], lastPageCompact: 0 }

	const midLayout: TableauDelibPageLayout = { withUeBlocks: false, withFooter: false }

	let firstTake = Math.min(capFirst, total)
	let lastPageCompact = 0

	// Tous les étudiants tiennent sur la 1ʳᵉ sans récap → garder un max sur page 1, le reste avec récap
	if (firstTake >= total && total > 1) {
		let bestFirst = 0
		for (let n = Math.min(capFirst, total - 1); n >= 1; n--) {
			const lastRows = rows.slice(n, total)
			if (lastPageFitsWithBestCompact(sourceRoot, wrapper, total - n, lastRows, footerSource)) {
				bestFirst = n
				break
			}
		}
		firstTake = bestFirst > 0 ? bestFirst : total - 1
	}

	const splits: number[] = [firstTake]
	let offset = firstTake
	let remaining = total - firstTake

	while (remaining > 0) {
		const sliceRows = rows.slice(offset, offset + remaining)
		const maxOnLast = measureTableauDelibMaxRowsOnLastPage(
			sourceRoot,
			wrapper,
			footerSource,
			sliceRows
		)

		if (maxOnLast >= remaining) {
			splits.push(remaining)
			lastPageCompact = findTableauFooterCompactLevelForLastPage(
				sourceRoot,
				wrapper,
				remaining,
				sliceRows,
				footerSource
			)
			break
		}

		const reservedLast = Math.max(1, maxOnLast)
		let midTake = 0

		for (let m = Math.min(capMiddle, remaining - 1); m >= 1; m--) {
			const after = remaining - m
			const midRows = rows.slice(offset, offset + m)
			const lastRows = rows.slice(offset + m, offset + remaining)
			if (
				tableauDelibPageFitsRowCount(
					sourceRoot,
					wrapper,
					m,
					midLayout,
					null,
					midRows
				) &&
				lastPageFitsWithBestCompact(sourceRoot, wrapper, after, lastRows, footerSource)
			) {
				midTake = m
				break
			}
		}

		if (midTake <= 0) {
			midTake = Math.min(capMiddle, Math.max(1, remaining - reservedLast))
		}

		splits.push(midTake)
		offset += midTake
		remaining -= midTake
	}

	if (lastPageCompact === 0 && splits.length > 1) {
		const lastCount = splits[splits.length - 1]
		const lastRows = rows.slice(total - lastCount, total)
		lastPageCompact = findTableauFooterCompactLevelForLastPage(
			sourceRoot,
			wrapper,
			lastCount,
			lastRows,
			footerSource
		)
	}

	return { splits, lastPageCompact }
}

/** Numérotation alignée sur la ligne « Delib. … » (dans le DOM, pas en overlay jsPDF). */
function setTableauDeliberationPageLabel(
	pageEl: HTMLElement,
	pageOneBased: number,
	totalPages: number
) {
	const footer = pageEl.querySelector('.doc-footer')
	if (!footer) return

	let pageSpan = footer.querySelector('.doc-footer-page') as HTMLElement | null
	if (!pageSpan) {
		pageSpan = document.createElement('span')
		pageSpan.className = 'doc-footer-page'
		footer.appendChild(pageSpan)
	}
	pageSpan.textContent = `${pageOneBased}/${totalPages}`
}

/** Ajoute récap/signatures dans le corps de page (avant la ligne « Delib. … » fixe en bas). */
function appendTableauDeliberationFooterSection(
	pageEl: HTMLElement,
	footerSource: HTMLElement
) {
	const body = pageEl.querySelector('.delib-body')
	const target = body || pageEl
	target.appendChild(footerSource.cloneNode(true) as HTMLElement)
}

/**
 * Pagination paysage dynamique :
 * - mesure DOM réelle (UE, récap/signatures, hauteur de ligne)
 * - 1 page si tout tient ; sinon 1ʳᵉ page remplie au max, reste sur les suivantes (ex. 28 → 27+1)
 * - récap/signatures toujours sur la dernière page, ancrés en bas
 */
function paginateTableauDeliberationDocument(hostEl: HTMLElement): HTMLElement[] {
	const sourceRoot = hostEl.querySelector('.tableau-delib-root') as HTMLElement | null
	if (!sourceRoot) return [hostEl]

	const originalTbody = sourceRoot.querySelector('table.tableau-deliberation-table tbody')
	const originalRows = Array.from(
		originalTbody?.querySelectorAll('tr') || []
	) as HTMLTableRowElement[]
	if (originalRows.length === 0) return [sourceRoot]

	const footerSource = sourceRoot.querySelector('.delib-footer-section') as HTMLElement | null
	const totalStudents = originalRows.length

	const host = sourceRoot.parentElement
	if (!host) return [sourceRoot]

	const wrapper = document.createElement('div')
	wrapper.className = 'paginated-wrapper-landscape-tableau'
	wrapper.style.width = '297mm'
	wrapper.style.background = '#ffffff'
	wrapper.style.position = 'absolute'
	wrapper.style.left = '-9999px'
	wrapper.style.top = '0'
	host.appendChild(wrapper)

	const singleCompact = findTableauFooterCompactLevelForSinglePage(
		sourceRoot,
		wrapper,
		totalStudents,
		originalRows,
		footerSource
	)
	if (singleCompact !== null) {
		applyTableauFooterCompactLevel(sourceRoot, singleCompact)
		applyTableauFooterAnchorBottom(sourceRoot, false)
		wrapper.remove()
		return [sourceRoot]
	}
	applyTableauFooterCompactLevel(sourceRoot, 0)
	applyTableauFooterAnchorBottom(sourceRoot, false)

	const capFirst = measureTableauDelibMaxRows(
		sourceRoot,
		wrapper,
		{ withUeBlocks: true, withFooter: false },
		null,
		originalRows
	)
	const capMiddle = measureTableauDelibMaxRows(
		sourceRoot,
		wrapper,
		{ withUeBlocks: false, withFooter: false },
		null,
		originalRows
	)
	const capLast = measureTableauDelibMaxRowsOnLastPage(
		sourceRoot,
		wrapper,
		footerSource,
		originalRows
	)

	if (capFirst < 1 || capMiddle < 1 || capLast < 1) {
		wrapper.remove()
		return [sourceRoot]
	}

	const planned = planTableauDelibPageSplits(
		sourceRoot,
		wrapper,
		originalRows,
		footerSource,
		capFirst,
		capMiddle
	)
	let splits = planned.splits
	let lastPageCompact = planned.lastPageCompact

	// Valide chaque tranche ; si une page déborde, réduit et réinjecte le surplus
	const validated: number[] = []
	let rowOffset = 0
	for (let i = 0; i < splits.length; i++) {
		const isFirst = i === 0
		const isLast = i === splits.length - 1
		let count = splits[i]

		const layout: TableauDelibPageLayout = {
			withUeBlocks: isFirst,
			withFooter: isLast,
		}

		while (count > 0) {
			const sliceRows = originalRows.slice(rowOffset, rowOffset + count)
			const fits = isLast
				? lastPageFitsWithBestCompact(
						sourceRoot,
						wrapper,
						count,
						sliceRows.length > 0 ? sliceRows : originalRows,
						footerSource
					)
				: tableauDelibPageFitsRowCount(
						sourceRoot,
						wrapper,
						count,
						layout,
						null,
						sliceRows.length > 0 ? sliceRows : originalRows
					)
			if (fits) {
				if (isLast) {
					lastPageCompact = findTableauFooterCompactLevelForLastPage(
						sourceRoot,
						wrapper,
						count,
						sliceRows.length > 0 ? sliceRows : originalRows,
						footerSource
					)
				}
				validated.push(count)
				rowOffset += count
				break
			}
			count--
		}

		if (count === 0) {
			// Ligne seule ne tient pas : forcer sur une page dédiée (cas extrême)
			validated.push(1)
			rowOffset += 1
		}
	}

	// Lignes restantes : tenter d'abord une page finale avec pied, sinon page intermédiaire
	while (rowOffset < totalStudents) {
		const remaining = totalStudents - rowOffset
		let placed = false

		let lastTry = remaining
		while (lastTry > 0) {
			const sliceRows = originalRows.slice(rowOffset, rowOffset + lastTry)
			if (lastPageFitsWithBestCompact(sourceRoot, wrapper, lastTry, sliceRows, footerSource)) {
				lastPageCompact = findTableauFooterCompactLevelForLastPage(
					sourceRoot,
					wrapper,
					lastTry,
					sliceRows,
					footerSource
				)
				validated.push(lastTry)
				rowOffset += lastTry
				placed = true
				break
			}
			lastTry--
		}
		if (placed) continue

		let midTry = Math.min(capMiddle, remaining)
		const midLayout: TableauDelibPageLayout = { withUeBlocks: false, withFooter: false }
		while (midTry > 0) {
			const sliceRows = originalRows.slice(rowOffset, rowOffset + midTry)
			if (
				tableauDelibPageFitsRowCount(
					sourceRoot,
					wrapper,
					midTry,
					midLayout,
					null,
					sliceRows
				)
			) {
				validated.push(midTry)
				rowOffset += midTry
				placed = true
				break
			}
			midTry--
		}
		if (!placed) {
			validated.push(1)
			rowOffset += 1
		}
	}

	splits = validated

	if (splits.length > 1) {
		const lastCount = splits[splits.length - 1]
		const lastRows = originalRows.slice(totalStudents - lastCount, totalStudents)
		lastPageCompact = findTableauFooterCompactLevelForLastPage(
			sourceRoot,
			wrapper,
			lastCount,
			lastRows,
			footerSource
		)
	}

	const pages: HTMLElement[] = []
	rowOffset = 0

	for (let i = 0; i < splits.length; i++) {
		const count = splits[i]
		const isFirst = i === 0
		const isLast = i === splits.length - 1
		const isMultiPage = splits.length > 1
		const pageCompact = isLast ? lastPageCompact : 0
		const anchorBottom = isLast && isMultiPage

		applyTableauFooterCompactLevel(sourceRoot, pageCompact)
		applyTableauFooterAnchorBottom(sourceRoot, anchorBottom)

		const skeleton = mountTableauDelibMeasurePage(
			sourceRoot,
			wrapper,
			{ withUeBlocks: isFirst, withFooter: isLast },
			isLast ? footerSource : null,
			pageCompact,
			anchorBottom
		)
		if (!skeleton) continue

		for (let j = 0; j < count; j++) {
			skeleton.tbody.appendChild(
				originalRows[rowOffset + j].cloneNode(true) as HTMLTableRowElement
			)
		}
		rowOffset += count

		// Sécurité : retirer les lignes excédentaires (jamais de chevauchement avec Delib. …)
		const trimmed = trimTableauDelibPageOverflowRows(skeleton, isLast)
		if (trimmed > 0) {
			rowOffset -= trimmed
			pages.push(skeleton.page)
			splits.splice(i + 1, 0, trimmed)
			continue
		}

		pages.push(skeleton.page)
	}

	sourceRoot.remove()
	wrapper.style.position = ''
	return pages.length > 0 ? pages : [sourceRoot]
}

async function captureTableauDeliberationPagesToPdf(host: HTMLElement): Promise<jsPDF> {
	const pdf = new jsPDF({
		orientation: 'landscape',
		unit: 'mm',
		format: 'a4',
	})

	const pdfW = pdf.internal.pageSize.getWidth()
	const pdfH = pdf.internal.pageSize.getHeight()

	const paginatedPages = paginateTableauDeliberationDocument(host)
	const totalPages = paginatedPages.length

	for (let i = 0; i < totalPages; i++) {
		if (i > 0) pdf.addPage()
		const pageEl = paginatedPages[i]
		setA4LandscapePageBox(pageEl)
		setTableauDeliberationPageLabel(pageEl, i + 1, totalPages)
		await new Promise((resolve) => setTimeout(resolve, 50))

		const pageCanvas = await html2canvas(pageEl, {
			scale: PDF_CANVAS_SCALE,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#ffffff',
			width: pageEl.offsetWidth,
			height: pageEl.offsetHeight,
			windowWidth: pageEl.offsetWidth,
			windowHeight: pageEl.offsetHeight,
			logging: false,
		})

		const pxPerMm = pageCanvas.width / pdfW
		const pageHeightMm = pxToMm(pageCanvas.height, pxPerMm)
		pdf.addImage(
			pageCanvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
			'JPEG',
			0,
			0,
			pdfW,
			Math.min(pageHeightMm, pdfH)
		)
	}

	return pdf
}

/** PDF tableau à partir de props DeliberationTemplate (mock / test layout). */
export async function generateTableauDeliberationPDFFromProps(
	templateProps: Record<string, unknown>,
	filename = 'Tableau délibération MOCK.pdf'
) {
	const host = createTableauDeliberationPdfHost()
	document.body.appendChild(host)

	const componentContainer = document.createElement('div')
	host.appendChild(componentContainer)

	const { createApp } = await import('vue')
	const TableauDeliberationFromProps = (
		await import('@/components/deliberation/templates/TableauDeliberationFromProps.vue')
	).default

	const app = createApp(TableauDeliberationFromProps, { templateProps })
	app.mount(componentContainer)
	await nextTick()
	await new Promise((resolve) => setTimeout(resolve, 400))

	if (!host.querySelector('.tableau-delib-root')) {
		document.body.removeChild(host)
		app.unmount()
		throw new Error("Élément tableau-delib-root introuvable.")
	}

	const preparingToastId = toast.loading('Génération du PDF mock…', { autoClose: false })
	try {
		const pdf = await captureTableauDeliberationPagesToPdf(host)
		toast.update(preparingToastId, {
			render: 'Téléchargement du PDF…',
			type: 'success',
			isLoading: false,
			autoClose: 1500,
		})
		pdf.save(filename)
	} catch (e) {
		toast.update(preparingToastId, {
			render: 'Erreur lors de la génération du PDF.',
			type: 'error',
			isLoading: false,
			autoClose: 4000,
		})
		throw e
	} finally {
		document.body.removeChild(host)
		app.unmount()
	}
}

function createTableauDeliberationPdfHost(): HTMLDivElement {
	const host = document.createElement('div')
	host.style.position = 'absolute'
	host.style.left = '-9999px'
	host.style.top = '0'
	host.style.width = '297mm'
	host.style.background = '#ffffff'
	host.style.boxSizing = 'border-box'
	return host
}

async function generateTableauDeliberationPDF(
	deliberation: Deliberation,
	exportFilter?: import('@/types/documentExport').TableauDeliberationExportFilter
) {
	const host = createTableauDeliberationPdfHost()
	document.body.appendChild(host)

	const componentContainer = document.createElement('div')
	host.appendChild(componentContainer)

	const { createApp } = await import('vue')
	const TableauDeliberationDocument = (
		await import('@/components/deliberation/templates/TableauDeliberationDocument.vue')
	).default

	const app = createApp(TableauDeliberationDocument, { deliberation, exportFilter })
	const vm = app.mount(componentContainer) as any
	await nextTick()

	const preparingToastId = toast.loading('Préparation du tableau de délibération…', {
		autoClose: false,
	})

	try {
		await waitUntilReadyOrThrow({
			hostEl: host,
			componentVm: vm,
			maxAttempts: 600,
			errorMessage: "Timeout: données du tableau de délibération non prêtes.",
		})
		await new Promise((resolve) => setTimeout(resolve, 300))
		toast.update(preparingToastId, {
			render: 'Génération du PDF…',
			type: 'default',
			isLoading: true,
			autoClose: false,
		})
	} catch {
		toast.update(preparingToastId, {
			render: 'Impossible de générer le PDF (chargement trop long).',
			type: 'error',
			isLoading: false,
			autoClose: 4000,
		})
		document.body.removeChild(host)
		app.unmount()
		return
	}

	if (!host.querySelector('.tableau-delib-root')) {
		toast.error("L'élément à imprimer est introuvable.")
		document.body.removeChild(host)
		app.unmount()
		return
	}

	const semestreNum = deliberation.semestre?.numero || ''
	const sessionCode = deliberation.session?.code || deliberation.session?.nom || ''
	const typeSession = getDeliberationSessionLabel(deliberation.type_deliberation)
	const filename = `Tableau délibération ${typeSession} S${semestreNum}${sessionCode ? ' ' + sessionCode : ''}.pdf`

	try {
		const pdf = await captureTableauDeliberationPagesToPdf(host)
		toast.update(preparingToastId, {
			render: 'Téléchargement du PDF…',
			type: 'success',
			isLoading: false,
			autoClose: 1500,
		})
		pdf.save(filename)
	} catch {
		toast.update(preparingToastId, {
			render: 'Erreur lors de la génération du PDF.',
			type: 'error',
			isLoading: false,
			autoClose: 4000,
		})
	} finally {
		document.body.removeChild(host)
		app.unmount()
	}
}

export async function generateDeliberationDocumentsPDF(
	deliberation: Deliberation,
	documentType: string,
	tableauOptions?: import('@/types/documentExport').TableauDeliberationExportFilter
) {
	try {
		if (documentType === 'resultats') {
			await generateResultatsPDF(deliberation)
		} else if (documentType === 'rattrapage') {
			await generateRattrapagePDF(deliberation)
		} else if (documentType === 'tableau') {
			await generateTableauDeliberationPDF(deliberation, tableauOptions)
		}
	} catch (error) {
		console.error('Erreur lors de la génération du PDF:', error)
		throw error
	}
}

async function waitUntilReadyOrThrow(options: {
	hostEl: HTMLElement
	componentVm: any
	maxAttempts?: number
	intervalMs?: number
	errorMessage: string
}) {
	const maxAttempts = options.maxAttempts ?? 120 // ~12s
	const intervalMs = options.intervalMs ?? 100

	for (let attempts = 0; attempts < maxAttempts; attempts++) {
		const vmReady = (() => {
			const r = options.componentVm?.isReady
			if (typeof r === 'boolean') return r
			if (r && typeof r === 'object' && 'value' in r) return Boolean(r.value)
			return false
		})()

		// DOM fallback: no loading message + at least one table rendered
		const container = options.hostEl.querySelector('.document-container')
		const isLoading =
			Boolean(options.hostEl.querySelector('.loading-message')) ||
			Boolean(options.hostEl.querySelector('.loading'))
		const hasTable = Boolean(container?.querySelector('table'))

		if ((vmReady || (!isLoading && hasTable))) return
		await new Promise((resolve) => setTimeout(resolve, intervalMs))
	}

	throw new Error(options.errorMessage)
}

async function generateResultatsPDF(deliberation: Deliberation) {
	// Créer un container off-screen pour le document
	const host = document.createElement('div')
	host.style.position = 'absolute'
	host.style.left = '-9999px'
	host.style.top = '0'
	host.style.width = '210mm'
	host.style.background = '#ffffff'
	host.style.boxSizing = 'border-box'
	document.body.appendChild(host)

	// Créer un élément pour le composant Vue
	const componentContainer = document.createElement('div')
	host.appendChild(componentContainer)

	// Importer et monter le composant Vue
	const { createApp } = await import('vue')
	const ResultatsSessionDeliberation = (await import('@/components/deliberation/templates/ResultatsSessionDeliberation.vue')).default

	const app = createApp(ResultatsSessionDeliberation, {
		deliberation
	})

	const vm = app.mount(componentContainer) as any
	await nextTick()

	// Ne jamais exporter si les données ne sont pas prêtes.
	const preparingToastId = toast.loading('Préparation du document (chargement des données)...', {
		autoClose: false
	})
	try {
		await waitUntilReadyOrThrow({
			hostEl: host,
			componentVm: vm,
			maxAttempts: 6000, // ~600s
			errorMessage: "Timeout: données du document 'Résultats' non prêtes."
		})
		// Petit délai pour stabiliser le rendu avant capture.
		await new Promise((resolve) => setTimeout(resolve, 250))
		toast.update(preparingToastId, {
			render: 'Génération du PDF...',
			type: 'default',
			isLoading: true,
			autoClose: false
		})
	} catch (e) {
		toast.update(preparingToastId, {
			render: 'Impossible de générer le PDF (données trop longues à charger).',
			type: 'error',
			isLoading: false,
			autoClose: 4000
		})
		document.body.removeChild(host)
		app.unmount()
		return
	}

	const sheetEl = host.querySelector('.document-container') as HTMLElement
	if (!sheetEl) {
		toast.error("L'élément à imprimer est introuvable.")
		document.body.removeChild(host)
		app.unmount()
		return
	}

	// Générer le PDF
	const pdf = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
		format: 'a4'
	})

	const pdfW = pdf.internal.pageSize.getWidth()
	const pdfH = pdf.internal.pageSize.getHeight()

	// Paginer intelligemment (tableau découpé par lignes + zone signature réservée)
	const paginatedPages = buildPaginatedPages(sheetEl)
	const pages = paginatedPages.length > 0 ? paginatedPages : (Array.from(sheetEl.querySelectorAll('.page')) as HTMLElement[])
	
	if (pages.length > 0) {
		const totalPages = pages.length
		// Générer chaque page séparément
		for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
			const pageEl = pages[pageIndex] as HTMLElement

			// Stabiliser le layout: A4 fixe, pas d'auto-slicing.
			setA4PageBox(pageEl)
			await new Promise(resolve => setTimeout(resolve, 50))

			const pageCanvas = await html2canvas(pageEl, {
				scale: PDF_CANVAS_SCALE,
				useCORS: true,
				allowTaint: true,
				backgroundColor: '#ffffff',
				height: pageEl.offsetHeight,
				width: pageEl.offsetWidth,
				windowWidth: pageEl.offsetWidth,
				windowHeight: pageEl.offsetHeight,
				logging: false
			})

			const pxPerMm = pageCanvas.width / pdfW
			const pageHeightMm = pxToMm(pageCanvas.height, pxPerMm)

			// Ne pas ajouter de page pour la première
			if (pageIndex > 0) {
				pdf.addPage()
			}

			// Chaque page est déjà en A4: on l'insère pleine page.
			pdf.addImage(
				pageCanvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
				'JPEG',
				0,
				0,
				pdfW,
				// Si l'image est très légèrement plus grande (arrondis), on force à la hauteur A4.
				Math.min(pageHeightMm, pdfH)
			)
			addPdfPageNumberBottomRight(pdf, pageIndex + 1, totalPages)
		}
	} else {
		// Fallback : générer normalement si pas de pages séparées
		const bodyCanvas = await html2canvas(sheetEl, {
			scale: PDF_CANVAS_SCALE,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#ffffff',
			logging: false
		})

		const pxPerMm = bodyCanvas.width / pdfW
		const bodyHeightMm = pxToMm(bodyCanvas.height, pxPerMm)
		const usableHeightMm = pdfH - 20

		if (bodyHeightMm <= usableHeightMm) {
			pdf.addImage(
				bodyCanvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
				'JPEG',
				0,
				0,
				pdfW,
				bodyHeightMm
			)
			addPdfPageNumberBottomRight(pdf, 1, 1)
		} else {
			const numPages = Math.ceil(bodyHeightMm / usableHeightMm)
			for (let i = 0; i < numPages; i++) {
				if (i > 0) pdf.addPage()

				const srcY = i * usableHeightMm * pxPerMm
				const srcH = Math.min(
					usableHeightMm * pxPerMm,
					bodyCanvas.height - srcY
				)

				const tempCanvas = document.createElement('canvas')
				tempCanvas.width = bodyCanvas.width
				tempCanvas.height = srcH
				const tempCtx = tempCanvas.getContext('2d')!
				tempCtx.drawImage(
					bodyCanvas,
					0,
					srcY,
					bodyCanvas.width,
					srcH,
					0,
					0,
					bodyCanvas.width,
					srcH
				)

				const sliceH = pxToMm(srcH, pxPerMm)
				pdf.addImage(
					tempCanvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
					'JPEG',
					0,
					0,
					pdfW,
					sliceH
				)
				addPdfPageNumberBottomRight(pdf, i + 1, numPages)
			}
		}
	}

	// Nettoyer
	document.body.removeChild(host)
	app.unmount()

	// Sauvegarder le PDF avec le format : "Résultats 1ère session Semestre 1 M2 Jan25"
	const filename = buildResultatsSessionPdfFilename(deliberation)

	toast.update(preparingToastId, {
		render: 'Téléchargement du PDF...',
		type: 'success',
		isLoading: false,
		autoClose: 1500
	})
	pdf.save(filename)
}

async function generateRattrapagePDF(deliberation: Deliberation) {
	// Créer un container off-screen pour le document (format paysage)
	const host = document.createElement('div')
	host.style.position = 'absolute'
	host.style.left = '-9999px'
	host.style.top = '0'
	host.style.width = '297mm'
	host.style.background = '#ffffff'
	host.style.boxSizing = 'border-box'
	document.body.appendChild(host)

	// Créer un élément pour le composant Vue
	const componentContainer = document.createElement('div')
	host.appendChild(componentContainer)

	// Importer et monter le composant Vue
	const { createApp } = await import('vue')
	const FeuilleRattrapage = (await import('@/components/deliberation/templates/FeuilleRattrapage.vue')).default

	const app = createApp(FeuilleRattrapage, {
		deliberation
	})

	const vm = app.mount(componentContainer) as any
	await nextTick()

	const outcomeLabels = getDeliberationOutcomeDocumentLabels(deliberation.type_deliberation)

	// Ne jamais exporter si les données ne sont pas prêtes.
	const preparingToastId = toast.loading('Préparation du document (chargement des données)...', {
		autoClose: false
	})
	try {
		await waitUntilReadyOrThrow({
			hostEl: host,
			componentVm: vm,
			maxAttempts: 600, // ~60s
			errorMessage: `Timeout: données du document '${outcomeLabels.feuilleModalTitle}' non prêtes.`
		})
		await new Promise((resolve) => setTimeout(resolve, 250))
		toast.update(preparingToastId, {
			render: 'Génération du PDF...',
			type: 'default',
			isLoading: true,
			autoClose: false
		})
	} catch (e) {
		toast.update(preparingToastId, {
			render: 'Impossible de générer le PDF (données trop longues à charger).',
			type: 'error',
			isLoading: false,
			autoClose: 4000
		})
		document.body.removeChild(host)
		app.unmount()
		return
	}

	const sheetEl = host.querySelector('.document-container') as HTMLElement
	if (!sheetEl) {
		toast.error("L'élément à imprimer est introuvable.")
		document.body.removeChild(host)
		app.unmount()
		return
	}

	// Générer le PDF en paysage
	const pdf = new jsPDF({
		orientation: 'landscape',
		unit: 'mm',
		format: 'a4'
	})

	const pdfW = pdf.internal.pageSize.getWidth()
	const pdfH = pdf.internal.pageSize.getHeight()

	// Paginer par lot d’EC puis par lignes (en-tête répétée sur chaque page).
	const paginatedPages = paginateRattrapageFullDocument(sheetEl)
	const totalRattrapagePages = paginatedPages.length

	for (let i = 0; i < totalRattrapagePages; i++) {
		if (i > 0) pdf.addPage()
		const pageEl = paginatedPages[i]
		setA4LandscapePageBox(pageEl)
		await new Promise((resolve) => setTimeout(resolve, 50))

		const pageCanvas = await html2canvas(pageEl, {
			scale: PDF_CANVAS_SCALE,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#ffffff',
			width: pageEl.offsetWidth,
			height: pageEl.offsetHeight,
			windowWidth: pageEl.offsetWidth,
			windowHeight: pageEl.offsetHeight,
			logging: false
		})

		const pxPerMm = pageCanvas.width / pdfW
		const pageHeightMm = pxToMm(pageCanvas.height, pxPerMm)
		pdf.addImage(
			pageCanvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
			'JPEG',
			0,
			0,
			pdfW,
			Math.min(pageHeightMm, pdfH)
		)
		addPdfPageNumberBottomRight(pdf, i + 1, totalRattrapagePages)
	}

	// Nettoyer
	document.body.removeChild(host)
	app.unmount()

	// Sauvegarder le PDF — libellé selon 1re (rattrapage) ou 2e délibération (EC à reprendre)
	const filename = buildFeuilleOutcomePdfFilename(deliberation)

	toast.update(preparingToastId, {
		render: 'Téléchargement du PDF...',
		type: 'success',
		isLoading: false,
		autoClose: 1500
	})
	pdf.save(filename)
}

/** Attend que le composant document (defineExpose isReady) soit prêt pour l’aperçu. */
export async function waitForDeliberationDocumentReady(
	componentVm: { isReady?: boolean | { value: boolean }; documentRef?: { value?: HTMLElement | null } | HTMLElement | null } | null,
	options?: { maxAttempts?: number; intervalMs?: number }
): Promise<void> {
	const maxAttempts = options?.maxAttempts ?? 600
	const intervalMs = options?.intervalMs ?? 100

	for (let attempts = 0; attempts < maxAttempts; attempts++) {
		const readyRef = componentVm?.isReady
		const vmReady =
			typeof readyRef === 'boolean'
				? readyRef
				: Boolean(readyRef && typeof readyRef === 'object' && 'value' in readyRef && readyRef.value)

		const docRef = componentVm?.documentRef
		const docEl =
			docRef && typeof docRef === 'object' && 'value' in docRef
				? docRef.value
				: (docRef as HTMLElement | null | undefined)

		const hasRenderedContent = Boolean(
			docEl?.querySelector?.(
				'.page, .rattrapage-page-frame, .tableau-delib-root, table.results-table, table.rattrapage-table, table.tableau-deliberation-table'
			)
		)

		if (vmReady && hasRenderedContent) return
		await new Promise((resolve) => setTimeout(resolve, intervalMs))
	}

	throw new Error('Timeout : chargement de l’aperçu du document')
}

/**
 * Applique la même pagination que le PDF téléchargé (aperçu multi-pages lisible).
 * @returns nombre de pages générées
 */
export function applyDeliberationDocumentPreviewPagination(
	hostEl: HTMLElement,
	documentType: string
): number {
	if (documentType === 'resultats') {
		const sheet = hostEl.querySelector('.document-container') as HTMLElement | null
		if (!sheet) return 0

		const pages = buildPaginatedPages(sheet)
		const finalPages =
			pages.length > 0
				? pages
				: (Array.from(sheet.querySelectorAll('.page')) as HTMLElement[])

		for (const page of finalPages) {
			setA4PageBox(page)
		}
		return finalPages.length
	}

	if (documentType === 'rattrapage') {
		const sheet = hostEl.querySelector('.document-container') as HTMLElement | null
		if (!sheet) return 0

		const pages = paginateRattrapageFullDocument(sheet)
		for (const page of pages) {
			setA4LandscapePageBox(page)
		}
		return pages.length
	}

	if (documentType === 'tableau') {
		const pages = paginateTableauDeliberationDocument(hostEl)
		for (let i = 0; i < pages.length; i++) {
			const page = pages[i]
			setA4LandscapePageBox(page)
			setTableauDeliberationPageLabel(page, i + 1, pages.length)
		}
		return pages.length
	}

	return 0
}

